import { Box } from '@mui/material';
import type { Property } from 'csstype';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { FC, PropsWithChildren } from 'react';
import {
  FullscreenControl,
  type LngLatBoundsLike,
  Map as Mapbox,
  NavigationControl,
  type PaddingOptions,
  type PointLike,
  type ViewState,
} from 'react-map-gl/mapbox';

export const MAP_PADDING = 16;

interface FitBoundsOptions {
  maxZoom?: number;
  minZoom?: number;
  offset?: PointLike;
  padding?: number | PaddingOptions;
}

export interface InitialViewState extends Partial<ViewState> {
  bounds?: LngLatBoundsLike;
  fitBoundsOptions?: FitBoundsOptions;
}

interface Props {
  height?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  initialViewState: InitialViewState;
  interactive?: boolean;
  mapStyle: string;
  maxHeight?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  maxWidth?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
  minHeight?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  minWidth?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
  width?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
}

export const MapView: FC<PropsWithChildren<Props>> = (props) =>
  (props.initialViewState.latitude && props.initialViewState.longitude) ||
  props.initialViewState.bounds ? (
    <MapWithBounds {...props} />
  ) : (
    <Box
      display="inline-grid"
      height={props.height ?? '100%'}
      width={props.height ?? '100%'}
    />
  );

export const MapWithBounds: FC<PropsWithChildren<Props>> = ({
  children,
  height = '100%',
  initialViewState,
  interactive = true,
  mapStyle,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  width = '100%',
}) => (
  <Box
    height={height}
    maxHeight={maxHeight}
    maxWidth={maxWidth}
    minHeight={minHeight}
    minWidth={minWidth}
    position="relative"
    sx={{
      '& .mapboxgl-ctrl-attrib': {
        fontSize: '0.5rem',
        opacity: 0.8,
      },
      '& .mapboxgl-ctrl-logo': {
        opacity: 0.6,
      },
    }}
    width={width}
  >
    <Mapbox
      cooperativeGestures={true}
      initialViewState={{
        ...initialViewState,
      }}
      interactive={interactive}
      mapboxAccessToken={import.meta.env.VITE_APP_MAPBOX_TOKEN as string}
      mapStyle={mapStyle}
      minZoom={11.5}
    >
      <FullscreenControl />
      <NavigationControl showCompass={false} />
      {children}
    </Mapbox>
  </Box>
);
